import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ShieldCheck, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import BackupExportCard from '@/components/admin/BackupExportCard';
import BackupImportCard from '@/components/admin/BackupImportCard';
import BackupEmailCard from '@/components/admin/BackupEmailCard';

const MAIN_KEYS = ['projects', 'uploaded_images', 'services', 'reviews', 'bookings', 'leads'];

export default function AdminBackup() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/backup/summary');
      setSummary(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento del riepilogo');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const cols = summary?.collections || {};

  return (
    <div data-testid="admin-backup-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl mb-2">Backup &amp; Ripristino</h1>
          <p className="text-muted-foreground">Salva una copia completa dei contenuti del sito (foto incluse) e ripristinala quando serve</p>
        </div>
        <Button variant="outline" onClick={fetchSummary} data-testid="backup-refresh-btn">
          <RefreshCw size={16} className="mr-2" />
          Aggiorna
        </Button>
      </div>

      <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded flex items-start gap-3">
        <ShieldCheck className="text-emerald-700 mt-0.5 shrink-0" size={20} />
        <p className="text-sm text-emerald-800">
          Consiglio: scarica un backup dopo ogni aggiunta di foto o modifica importante e conservalo su Google Drive / disco esterno.
          In caso di perdita dati potrai ripristinare tutto in un clic.
        </p>
      </div>

      {loading && !summary ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="animate-spin" size={32} /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="backup-summary">
          {MAIN_KEYS.map((key) => (
            <Card key={key}>
              <CardContent className="pt-4 pb-4">
                <p className="text-sm text-muted-foreground">{cols[key]?.label || key}</p>
                <p className="text-2xl font-bold" data-testid={`backup-count-${key}`}>{cols[key]?.count ?? 0}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <BackupExportCard summary={summary} />
        <BackupImportCard onRestored={fetchSummary} />
        <BackupEmailCard />
      </div>
    </div>
  );
}
